import React from 'react';
import type { FieldProps } from '../types';

/**
 * CheckboxField Component
 *
 * Renders checkbox group (multiple selection).
 */
export const CheckboxField: React.FC<FieldProps> = ({
  element,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  theme,
}) => {
  const properties = element.properties;
  const options = properties?.options || [];
  const selectedValues = Array.isArray(value) ? value : [];

  const handleToggle = (optionValue: string) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter(v => v !== optionValue)
      : [...selectedValues, optionValue];
    onChange(newValues);
  };

  return (
    <div className="mediform-field mediform-checkbox-field">
      {properties?.label && (
        <div className="mediform-field-label">
          {properties.label}
          {properties.required && <span className="mediform-required"> *</span>}
        </div>
      )}

      <div className="mediform-checkbox-group" role="group" aria-label={properties?.label}>
        {options.map((option) => {
          const optionId = `${element.id}-${option.value}`;
          const isChecked = selectedValues.includes(option.value);

          return (
            <label
              key={option.value}
              htmlFor={optionId}
              className={`mediform-checkbox-option ${isChecked ? 'mediform-checkbox-checked' : ''}`}
            >
              <input
                type="checkbox"
                id={optionId}
                name={element.id}
                value={option.value}
                checked={isChecked}
                onChange={() => handleToggle(option.value)}
                onBlur={onBlur}
                disabled={disabled}
                className="mediform-checkbox-input"
              />
              <span className="mediform-checkbox-label">{option.label}</span>
              {option.score !== undefined && (
                <span className="mediform-checkbox-score">({option.score})</span>
              )}
            </label>
          );
        })}
      </div>

      {error && (
        <div className="mediform-field-error" style={{ color: theme?.colors?.error }}>
          {error}
        </div>
      )}
    </div>
  );
};

/**
 * SingleCheckbox Component
 *
 * Renders a single checkbox (boolean value).
 */
export const SingleCheckbox: React.FC<FieldProps> = ({
  element,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  theme,
}) => {
  const properties = element.properties;
  const isChecked = Boolean(value);

  return (
    <div className="mediform-field mediform-single-checkbox-field">
      <label htmlFor={element.id} className="mediform-single-checkbox-label">
        <input
          type="checkbox"
          id={element.id}
          checked={isChecked}
          onChange={(e) => onChange(e.target.checked)}
          onBlur={onBlur}
          disabled={disabled}
          required={properties?.required}
          className="mediform-checkbox-input"
        />
        <span className="mediform-checkbox-text">
          {properties?.label}
          {properties?.required && <span className="mediform-required"> *</span>}
        </span>
      </label>

      {error && (
        <div className="mediform-field-error" style={{ color: theme?.colors?.error }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default CheckboxField;
